import { X } from "@phosphor-icons/react";
import clsx from "clsx";
import { Children } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";

import type { MouseEvent, ReactNode } from "react";

const tagContainerClassNames = {
  default: "bg-whiteGray border-black-200",
  danger: "text-red-600 bg-red-50 border-red-200",
};

type BaseFilterTagProps = {
  children: ReactNode;
  onRemove: () => void;
  variant?: "default" | "danger";
} & (
  | {
      dropdownMenuChildren: JSX.Element;
      isDropdownMenuOpen: boolean;
      setIsDropdownMenuOpen: (isOpen: boolean) => void;
    }
  | {
      dropdownMenuChildren?: never;
      isDropdownMenuOpen?: never;
      setIsDropdownMenuOpen?: never;
    }
);

function FilterTag({
  children,
  onRemove,
  variant = "default",
  dropdownMenuChildren,
  isDropdownMenuOpen,
  setIsDropdownMenuOpen,
}: BaseFilterTagProps) {
  const tagContent = (
    <>
      {children}

      <FilterTagChild
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <X />
      </FilterTagChild>
    </>
  );

  const containerClassname = clsx(
    "rounded-md flex overflow-hidden border h-6",
    tagContainerClassNames[variant],
  );

  if (dropdownMenuChildren) {
    return (
      <DropdownMenu
        open={isDropdownMenuOpen}
        onOpenChange={setIsDropdownMenuOpen}
      >
        <DropdownMenuTrigger
          className={containerClassname}
          onClick={(e) => e.preventDefault()}
        >
          {tagContent}
        </DropdownMenuTrigger>

        <DropdownMenuContent
          size="sm"
          align="start"
          className="min-w-[var(--radix-popper-anchor-width)]"
        >
          {dropdownMenuChildren}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return <div className={containerClassname}>{tagContent}</div>;
}

const tagChildClassName =
  "flex items-center gap-x-1 pr-2 has-[>svg:only-child]:pr-1 border-l border-[inherit] first:border-l-0 text-button-xs [&>svg]:size-4 [&>img]:size-4";

type FilterTagChildProps = {
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
};

function FilterTagChild({ children, onClick }: FilterTagChildProps) {
  const childrenArray = Children.toArray(children);
  const [firstChild] = childrenArray;
  // children starting with an icon get a smaller left padding
  const isFirstChildText =
    typeof firstChild === "string" || typeof firstChild === "number";

  const className = clsx(
    tagChildClassName,
    isFirstChildText ? "pl-2" : "pl-1",
    childrenArray.length === 1 && !isFirstChildText ? "" : "overflow-hidden",
  );

  const renderedChildren = childrenArray.map((child) =>
    typeof child === "string" ? (
      <span className="truncate" key={child} title={child}>
        {child}
      </span>
    ) : (
      child
    ),
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={clsx(className, "transition-colors hover:bg-black-200")}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onClick}
      >
        {renderedChildren}
      </button>
    );
  }

  return <div className={className}>{renderedChildren}</div>;
}

export { FilterTag, FilterTagChild };
